/* eslint-disable react-refresh/only-export-components -- co-locating Provider + hook for the names context is intentional and matches references-context */
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { RootState } from '@/context/store/store';
import {
  DEFAULT_NAMES_SCOPE,
  setScopedDocumentNames,
} from '@/context/store/slices/documentNamesSlice';

const EmptyNamesById: Record<string, string> = {};

/**
 * Names are read from the Redux `documentNames` slice, keyed by scope. Blocks
 * resolve their scope from this context so the main editor and any document
 * preview read/write independent keyspaces and never clobber one another.
 */
const NamesScopeContext = createContext<string>(DEFAULT_NAMES_SCOPE);

export function NamesProvider({
  children,
  scope,
  value,
}: {
  children: ReactNode;
  /** Scope key for the names this subtree reads/writes. Defaults to the editor scope. */
  scope?: string;
  /** Test-only shim — seeds the slice for this scope from an in-memory map. */
  value?: Map<string, string>;
}) {
  const dispatch = useDispatch();
  const effectiveScope = scope ?? DEFAULT_NAMES_SCOPE;

  useEffect(() => {
    if (!value) return;
    const record: Record<string, string> = {};
    for (const [id, name] of value) {
      record[id] = name;
    }
    dispatch(setScopedDocumentNames({ scope: effectiveScope, names: record }));
  }, [dispatch, value, effectiveScope]);

  return (
    <NamesScopeContext.Provider value={effectiveScope}>
      {children}
    </NamesScopeContext.Provider>
  );
}

export function useNodeName(id: string): string | null {
  const scope = useContext(NamesScopeContext);
  return useSelector(
    (state: RootState) => state.documentNames?.byScope?.[scope]?.[id] ?? null,
  );
}

export function useNamesMap(): Map<string, string> {
  const scope = useContext(NamesScopeContext);
  const byId = useSelector(
    (state: RootState) =>
      state.documentNames?.byScope?.[scope] ?? EmptyNamesById,
  );
  return useMemo(() => new Map(Object.entries(byId)), [byId]);
}
